import * as THREE from "three";

/**
 * Computes camera-relative WASD movement direction from key state
 * and OrbitControls. Reusable by both client and editor.
 *
 * The game uses X/Y as the horizontal plane (Z is up).
 */
export class WASDController {
    private wasMoving: boolean = false;
    private controls: any; // OrbitControls — used to get camera direction
    private keysPressed: Set<string>;
    /** Optional analog input (x right, y forward, each -1..1), e.g. a touch joystick. Wins over keys. */
    analog: { x: number; y: number } | null = null;

    constructor(controls: any, keysPressed: Set<string>) {
        this.controls = controls;
        this.keysPressed = keysPressed;
    }

    /**
     * Returns the camera-relative movement direction
     * as a normalized {x, y} object, or null if no WASD keys are pressed.
     */
    getDirection(): { x: number; y: number } | null {
        const keys = this.keysPressed;
        const hasW = keys.has("w");
        const hasA = keys.has("a");
        const hasS = keys.has("s");
        const hasD = keys.has("d");

        if (!this.analog && !hasW && !hasA && !hasS && !hasD) {
            return null;
        }

        // Build raw input direction in camera space
        let inputX = this.analog ? this.analog.x : 0;
        let inputY = this.analog ? this.analog.y : 0;
        if (!this.analog) {
            if (hasW) inputY += 1;
            if (hasS) inputY -= 1;
            if (hasA) inputX -= 1;
            if (hasD) inputX += 1;
        }

        // Normalize diagonal movement
        const len = Math.sqrt(inputX * inputX + inputY * inputY);
        if (len < 0.001) return null;
        inputX /= len;
        inputY /= len;

        // Compute forward direction from camera to orbit target, projected onto XY plane.
        // This works regardless of camera.up orientation.
        const camPos = this.controls.object.position;
        const target = this.controls.target;
        let forwardX = target.x - camPos.x;
        let forwardY = target.y - camPos.y;

        // Normalize the forward vector on the XY plane
        const fLen = Math.sqrt(forwardX * forwardX + forwardY * forwardY);
        if (fLen < 0.001) return null;
        forwardX /= fLen;
        forwardY /= fLen;

        // Right is perpendicular to forward (rotate 90 degrees CW in XY)
        const rightX = forwardY;
        const rightY = -forwardX;

        // Combine: direction = forward * inputY + right * inputX
        let dirX = forwardX * inputY + rightX * inputX;
        let dirY = forwardY * inputY + rightY * inputX;

        // Normalize
        const dirLen = Math.sqrt(dirX * dirX + dirY * dirY);
        if (dirLen < 0.001) return null;
        dirX /= dirLen;
        dirY /= dirLen;

        return { x: dirX, y: dirY };
    }

    /**
     * Returns true if spacebar is pressed (consumed on read).
     */
    consumeJump(): boolean {
        if (this.keysPressed.has(" ")) {
            this.keysPressed.delete(" ");
            return true;
        }
        return false;
    }

    /**
     * Call each frame. Invokes onMove/onStop/onJump callbacks based on key state.
     */
    update(
        delta: number,
        onMove: (dirX: number, dirY: number) => void,
        onStop: () => void,
        onJump?: () => void,
    ) {
        if (onJump && this.consumeJump()) {
            onJump();
        }

        const dir = this.getDirection();

        if (dir) {
            onMove(dir.x, dir.y);
            this.wasMoving = true;
        } else if (this.wasMoving) {
            onStop();
            this.wasMoving = false;
        }
    }
}
